import React, { useRef } from "react";
import FileUploadIcon from "@mui/icons-material/FileUpload";
import * as XLSX from "xlsx";
import { useAddUserWithUUID, useCheckDuplicates } from "../../hooks/useUser";
import { useCreateDoctorFromUser } from "../../hooks/useDoctorMasterData";
import doctorMasterDataService from "../../services/doctorMasterDataService";
import { toast } from "react-toastify";
import cityService from "../../services/cityService";
import countryService from "../../services/countryService";
import divisionService from "../../services/divisionService";
import roleService from "../../services/roleService";
import teamService from "../../services/teamService";
import {
  DIVISION_CONTROLLER,
  SALES_SUPERVISOR,
  TEAM_MEMBER,
} from "../../constants/roles";
import { createUserWithEmailAndPassword } from "firebase/auth";
import { adminAuth } from "../../config/firebaseAdminAuth";

export default function ImportUsers() {
  const { mutateAsync: addUserWithUUID } = useAddUserWithUUID();
  const { mutate: createDoctor } = useCreateDoctorFromUser();
  const { mutateAsync: checkDuplicates } = useCheckDuplicates();
  const fileInputRef = useRef(null);

  // Helper function to add delay between requests
  const delay = (ms) => new Promise(resolve => setTimeout(resolve, ms));

  const handleClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleDownloadTemplate = () => {
    // Create template headers
    const headers = [
      "First Name",
      "Family Name",
      "E-Mail Address",
      "UPI",
      "Division",
      "Sales Supervisor - group",
      "Country",
      "City",
      "Role",
      "Phone Number",
      "Specialty",
      "Medical License",
      "Organization",
    ];

    // Create workbook and worksheet
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.aoa_to_sheet([headers]);

    // Add worksheet to workbook
    XLSX.utils.book_append_sheet(wb, ws, "Users Template");

    // Save file
    XLSX.writeFile(wb, "create_users_template.xlsx");
  };

  const handleFileChange = async (event) => {
    const file = event.target.files[0];
    if (file) {
      try {
        const reader = new FileReader();
        reader.onload = async (e) => {
          const data = new Uint8Array(e.target.result);
          const workbook = XLSX.read(data, { type: "array" });
          const sheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[sheetName];
          const jsonData = XLSX.utils.sheet_to_json(worksheet);

          let successCount = 0;
          let duplicateCount = 0;
          let errorCount = 0;
          let currentRow = 0;

          // Process and upload each user
          for (const row of jsonData) {
            currentRow++;
            try {
              // Check for duplicates
              const duplicates = await checkDuplicates({
                UPI_num: row["UPI"],
                email_address: row["E-Mail Address"].toLowerCase(),
              });

              if (
                duplicates &&
                (duplicates.UPI_num || duplicates.email_address)
              ) {
                const duplicateFields = [];
                if (duplicates.UPI_num) duplicateFields.push("UPI");
                if (duplicates.email_address) duplicateFields.push("E-Mail Address");

                toast.warning(
                  `Row ${currentRow}: Skipping duplicate entry for ${row["First Name"]} ${row["Family Name"]}. ` +
                  `Duplicate ${duplicateFields.join(" and ")} found.`
                );
                duplicateCount++;
                continue;
              }

              // Validate required fields
              if (
                !row["First Name"] ||
                !row["Family Name"] ||
                !row["E-Mail Address"] ||
                !row["UPI"] ||
                !row["Role"]
              ) {
                toast.error(
                  `Row ${currentRow}: Missing required fields for ${row["First Name"]} ${row["Family Name"]}. Required fields: First Name, Family Name, E-Mail Address, UPI, Country, Role`
                );
                errorCount++;
                continue;
              }

              const countryId = await countryService.getIdByName(
                row["Country"].trim()
              );
              if (!countryId) {
                toast.error(
                  `Row ${currentRow}: Invalid country: ${row["Country"]} for user ${row["First Name"]} ${row["Family Name"]}`
                );
                errorCount++;
                continue;
              }

              let cityId = null;
              if (row["City"]) {
                cityId = await cityService.getIdByName(row["City"].trim());
                if (!cityId) {
                  toast.error(
                    `Row ${currentRow}: Invalid city: ${row["City"]} for user ${row["First Name"]} ${row["Family Name"]}`
                  );
                  errorCount++;
                  continue;
                }
              }

              const roleId = await roleService.getIdByName(row["Role"].trim());
              if (!roleId) {
                toast.error(
                  `Row ${currentRow}: Invalid role: ${row["Role"]} for user ${row["First Name"]} ${row["Family Name"]}`
                );
                errorCount++;
                continue;
              }

              // Get role name to check requirements
              const roleName = await roleService.getNameById(roleId);

              let divisionId = "";
              let teamId = "";

              if (
                [
                  DIVISION_CONTROLLER,
                  SALES_SUPERVISOR,
                  TEAM_MEMBER,
                ].includes(roleName)
              ) {
                if (!row["Division"]) {
                  toast.error(`Row ${currentRow}: Division is required for role ${roleName}`);
                  errorCount++;
                  continue;
                }
                divisionId = await divisionService.getIdByName(row["Division"]);
                if (!divisionId) {
                  toast.error(
                    `Row ${currentRow}: Invalid division: ${row["Division"]} for user ${row["First Name"]} ${row["Family Name"]}`
                  );
                  errorCount++;
                  continue;
                }
              }

              if ([SALES_SUPERVISOR, TEAM_MEMBER].includes(roleName)) {
                if (!row["Sales Supervisor - group"]) {
                  toast.error(`Row ${currentRow}: Team is required for role ${roleName}`);
                  errorCount++;
                  continue;
                }
                teamId = await teamService.getIdByName(
                  row["Sales Supervisor - group"]
                );
                if (!teamId) {
                  toast.error(
                    `Row ${currentRow}: Invalid team: ${row["Sales Supervisor - group"]} for user ${row["First Name"]} ${row["Family Name"]}`
                  );
                  errorCount++;
                  continue;
                }
              }

              // Generate a UUID for the user
              // const userId = crypto.randomUUID();
              const userCredential = await createUserWithEmailAndPassword(
                adminAuth,
                row["E-Mail Address"].toLowerCase(),
                "HfFFUKNdpg5gAHPj"
              );
              const userId = userCredential.user.uid;
              const passwordData =
                userCredential.user.reloadUserInfo.passwordHash;

              const userData = {
                first_name: row["First Name"],
                family_name: row["Family Name"],
                email_address: row["E-Mail Address"].toLowerCase(),
                password: passwordData, // Default password
                UPI_num: row["UPI"],
                division_id: divisionId || null,
                team_id: teamId || null,
                country_id: countryId,
                city_id: cityId,
                role_id: roleId,
                phone_number: row["Phone Number"] || "N/A",
                specialty: row["Specialty"] || "N/A",
                medical_license: row["Medical License"] || "N/A",
                organization: row["Organization"] || "Abbott",
                status: true,
                abbott_login: true,
              };

              // Create user directly in the collection without Firebase Auth
              await addUserWithUUID({ userId, userData });

              // Create doctor master data
              try {
                // Check if doctor already exists by email
                const existingDoctor = await doctorMasterDataService.getByEmail(userData.email_address);
                
                if (existingDoctor) {
                  toast.warning(
                    `Row ${currentRow}: User created but doctor master data already exists for ${userData.email_address}`
                  );
                } else {
                  await createDoctor({ ...userData, APO_number: row["UPI"] });
                }
                
                successCount++;
                toast.success(
                  `Row ${currentRow}: User created successfully: ${userData.email_address}`
                );
                // log the current index of the total length of the json data
                console.log(`Current index: ${currentRow} of ${jsonData.length}`);
                // Add delay between requests (30-60 seconds)
                const delayTime = 30000; // 30-60 seconds
                toast.info(`Waiting ${Math.round(delayTime/1000)} seconds before processing next user...`);
                await delay(delayTime);
              } catch (error) {
                toast.warning(
                  `Row ${currentRow}: User created but failed to create doctor master data for ${userData.email_address}`
                );
                
                // Add delay even for partial failures
                const delayTime = 30000; // 30-60 seconds
                toast.info(`Waiting ${Math.round(delayTime/1000)} seconds before processing next user...`);
                await delay(delayTime);
              }
            } catch (error) {
              toast.error(
                `Row ${currentRow}: Failed to process user ${row["First Name"]} ${row["Family Name"]}: ${error.message}`
              );
              errorCount++;
              
              // Add delay after errors too
              const delayTime = 30000; // 30-60 seconds
              toast.info(`Waiting ${Math.round(delayTime/1000)} seconds before processing next user...`);
              await delay(delayTime);
            }
          }

          // Show summary
          toast.success(
            `Import completed: ${successCount} users created, ` +
            `${duplicateCount} duplicates skipped, ` +
            `${errorCount} errors.`
          );

          event.target.value = "";
        };
        reader.readAsArrayBuffer(file);
      } catch (error) {
        toast.error(error.message || "Error importing users!");
      }
    }
  };

  return (
    <div className="flex items-center gap-5">
      <button
        onClick={handleDownloadTemplate}
        className="bg-yellowColor transition-colors duration-200 px-4 py-2  rounded-lg text-white font-medium flex gap-2 items-center"
      >
        <i className="fa-solid fa-file-arrow-down text-lg"></i>
        Download Template
      </button>

      <div>
        <input
          type="file"
          ref={fileInputRef}
          id="import-file"
          accept=".xlsx"
          onChange={handleFileChange}
          style={{ display: "none" }}
        />
        <label htmlFor="import-file">
          <button
            data-track="import-users-button"
            data-module="Users"
            type="button"
            onClick={handleClick}
            className="bg-green-600 hover:bg-green-700 transition-colors duration-200 px-4 py-2  rounded-lg text-white font-medium flex gap-2 items-center"
          >
            <FileUploadIcon />
            Import Users
          </button>
        </label>
      </div>
    </div>
  );
}
